import { useEffect, useRef, useState } from 'react';
import { Box, Icon } from '@mui/material';
import { TextField, TextFieldExtendedProps } from '@components/textField';
import { Percent } from '@assets/svgs';
import { getFloat } from '@utils/regex';
import { round } from '@utils/number';
import { AdditionalInfoLinkProps } from '@components/container/AdditionalInfoLink';

export const PercentField = ({
  value,
  externalIcon,
  onPressEndIcon,
  onChangeInput,
  ...props
}: Omit<TextFieldExtendedProps, 'value' | 'onChangeInput'> & {
  value: number | undefined;
  onChangeInput: (eventValue: number | undefined) => void;
  additionalInfoLink?: AdditionalInfoLinkProps;
}) => {
  const ref = useRef(null);
  const valueRef = useRef(value);
  const [innerValue, setInnerValue] = useState(typeof value === 'number' ? round(value * 100, 8).toString() : '');

  useEffect(() => {
    if (valueRef.current !== value) {
      setInnerValue(typeof value === 'number' ? round(value * 100, 8).toString() : '');
    }
    valueRef.current = value;
  }, [innerValue, value]);

  const handleValueDivider = (value: string) => {
    if (!value.endsWith('.') && !value.endsWith('-')) {
      let outputValue = value ? Number(value) / 100 : undefined;
      if (value.includes('.')) {
        const postDecimalLength = value.split('.')[1]?.length ?? 0;
        const precision = postDecimalLength + 2;
        outputValue = round(outputValue ?? 0 / 100, precision);
      }
      onChangeInput(outputValue);
    }
    setInnerValue(value);
  };

  const handleOnPressEndIcon = () => {
    if (ref.current) {
      (ref.current as HTMLElement).focus();
    }

    onPressEndIcon?.();
  };

  return (
    <Box
      sx={{
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'top',
        height: '100%',
        width: '100%',
        justifyContent: 'flex-end',
        verticalAlign: 'top',
      }}
    >
      <TextField
        data-testid='percent-field'
        handleOnPattern={getFloat}
        onChangeInput={handleValueDivider}
        endIcon={
          <Icon data-testid='percent-end-icon'>
            <Percent />
          </Icon>
        }
        externalIcon={externalIcon}
        inputRef={ref}
        value={innerValue}
        onPressEndIcon={handleOnPressEndIcon}
        {...props}
      />
    </Box>
  );
};
